/**
 * PlotTypeSelector Component
 *
 * This component provides a dropdown to select the plot type for speaker visualization.
 * Available options: Direct SPL, Total SPL, Relative Average Level, STI (when available)
 */
import React, { useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import Select from '../atoms/Select';
import InputLabel from '../atoms/InputLabel';
import { uiReducers } from '../../reduxModules/ducks/ui';
import { getCurrentValue } from 'reduxModules/selectors/containerSelectors';

interface PlotOption {
  value: string;
  label: string;
}

interface PlotTypeSelectorProps {
  show?: boolean;
  disabled?: boolean;
  label?: string;
}

const PlotTypeSelector: React.FC<PlotTypeSelectorProps> = ({ show = true, disabled = false, label }) => {
  const dispatch = useDispatch();

  // Get current device and plot type
  const currentDevice = useSelector(state => getCurrentValue(state, 'currentDevice'));
  const currentPlotType = useSelector(state => getCurrentValue(state, 'showMap'));

  // Get speaker plot data to check STI availability
  const speakerPlotData = useSelector((state: any) => {
    const areas = state?.container?.areas || {};
    const activeTab = state?.container?.activeTab;
    const currentArea = activeTab && areas[activeTab] ? areas[activeTab] : Object.values(areas)[0];
    return currentArea?.speakers?.plotData;
  });

  // Check if STI data is available
  const hasStiData = speakerPlotData?.stiWithNoiseMaskingMatrix != null;

  // Available plot types for speakers
  const plotOptions: PlotOption[] = useMemo(() => [
    { value: 'directSPL', label: 'Direct SPL' },
    // Total SPL intentionally omitted for PIG-584 — only Direct SPL is enabled for now.
    { value: 'relativeAverage', label: 'Relative Average Level' },
    // Only include STI if data is available
    ...(hasStiData ? [{ value: 'STI', label: 'Estimated STI' }] : [])
  ], [hasStiData]);

  // Valid speaker plot types (memoized to prevent re-renders)
  const validSpeakerPlotTypes = useMemo(() =>
    plotOptions.map(o => o.value),
    [plotOptions]
  );

  // Initialize or fix plot type when in speakers view
  useEffect(() => {
    if (currentDevice === 'speakers') {
      // If no plot type set, or if it's not a valid speaker plot type (e.g., 'micDistance' from microphones)
      if (!currentPlotType || !validSpeakerPlotTypes.includes(currentPlotType)) {
        // Set to default plot type (relativeAverage) for speakers
        dispatch(uiReducers.setShowMap('relativeAverage'));
      }
    }
  }, [currentDevice, currentPlotType, validSpeakerPlotTypes, dispatch]);

  // Auto-switch away from STI if it becomes unavailable
  useEffect(() => {
    if (currentDevice === 'speakers' && currentPlotType === 'STI' && !hasStiData) {
      // STI is selected but no longer available, switch to directSPL
      dispatch(uiReducers.setShowMap('directSPL'));
    }
  }, [currentDevice, currentPlotType, hasStiData, dispatch]);

  // Only show for speakers device. Re-enabled now that the Direct/Total SPL
  // legend labels (PIG-215) and colour scaling (PIG-219) are fixed.
  if (currentDevice !== 'speakers' || !show) {
    return null;
  }

  const handleChange = (event: any) => {
    const newPlotType = event.target.value;
    dispatch(uiReducers.setShowMap(newPlotType));
  };

  // Create display names for the dropdown
  const plotBubbles = plotOptions.reduce((acc, option) => {
    acc[option.value] = option.label;
    return acc;
  }, {} as Record<string, string>);

  return (
    <Box className="d-flex flex-column" sx={{ width: '100%' }}>
      {label && <InputLabel>{label}</InputLabel>}
      <Select
        value={currentPlotType || 'directSPL'}
        items={plotOptions.map(o => o.value)}
        onChange={handleChange}
        disabled={disabled}
        placeHolder="Select plot type"
        bubbles={plotBubbles}
      />
    </Box>
  );
};

export default PlotTypeSelector;